import * as yup from 'yup';
import { observable, action, makeObservable, runInAction } from 'mobx';
import moment from 'moment-timezone';
import { RootStore } from './root';
import { NotificationManager } from '../components/notification-manager';
import {
    FeaturedTypes,
    FeaturedTypesRegEx,
    ActiveQueryParam,
} from '../utils/const';
import { Lang, LangRegEx } from '../utils';
import { HttpMethod, CmsUserHistory, PaginatedList } from '../types';
import { MomentSchema } from '../utils/yup-schemas';

const MAX_DATE_RANGE_DAYS = 365;

const baseFeaturedItemFields = {
    backgroundImage: yup.string().required(),
    imageTitle: yup.string().nullable(),
    textTitle: yup.string().nullable(),
    title: yup.string().nullable(),
    subTitle: yup.string().nullable(),
    textColor: yup.string().nullable(),
    backgroundColor: yup.string().nullable(),
    titleColor: yup.string().nullable(),
    subtitleColor: yup.string().nullable(),
    buttonColor: yup.string().nullable(),
    cta: yup.string().nullable(),
    externalUrl: yup.string().nullable(),
};

export const yupFeaturedItems = yup
    .object(baseFeaturedItemFields)
    .required()
    .shape({
        language: yup.string<Lang>().required().matches(LangRegEx),
    });

export const yupFeatured = yup.object({
    id: yup.number().required(),
    type: yup.string<FeaturedTypes>().matches(FeaturedTypesRegEx).required(),
    gameType: yup.string().nullable(),
    gameId: yup.number().nullable(),
    geolocated: yup.boolean().nullable(),
    canBeDeleted: yup.boolean().required(),
    position: yup.number().nullable(),
    isActive: yup.boolean().required(),
    createdAt: yup.string().required(),
    timezone: yup.string().required(),
    startDate: yup.string().required(),
    endDate: yup.string().required(),
    appTarget: yup.string<'QC' | 'CA'>().nullable().default(null),
    promotionId: yup.number().nullable(),
    promotion: yup
        .object({
            id: yup.number().required(),
            items: yup
                .array(
                    yup.object({
                        title: yup.string().required(),
                        language: yup.string().required(),
                    })
                )
                .required(),
        })
        .nullable(),
    items: yup.array(yupFeaturedItems).required(),
});

export const yupPaginatedList = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(yupFeatured).default([]),
});

export const yupFeaturedFormItems = yup
    .object({
        ...baseFeaturedItemFields,
        imageTitle: yup.string().notRequired().nullable(),
        textTitle: yup.string().notRequired().nullable(),
        title: yup.string().notRequired().nullable(),
        subTitle: yup.string().notRequired().nullable(),
        cta: yup.string().notRequired().nullable(),
        externalUrl: yup.string().url().notRequired().nullable(),
    })
    .required()
    .shape({
        language: yup.string<'en' | 'fr'>().matches(/en|fr/).required(),
    });

export const yupFeaturedForm = yup.object({
    id: yup.number(),
    type: yup.string<FeaturedTypes>().matches(FeaturedTypesRegEx).required(),
    isActive: yup.boolean().required(),
    geolocated: yup.boolean().nullable(),
    isImageTitle: yup.boolean(),
    canBeDeleted: yup.boolean().required(),
    appTarget: yup.string<'QC' | 'CA'>().nullable().notRequired().default(null),
    gameTypeId: yup.string().nullable().notRequired(),
    gameId: yup
        .number()
        .nullable()
        .notRequired()
        .test('shouldBeDefined', 'Please select a game', function (v) {
            const type = this.resolve(yup.ref('type'));
            return !(type === 'games' && !v);
        }),
    gameType: yup.string().nullable().notRequired(),
    startDate: new MomentSchema().required(),
    endDate: new MomentSchema().required(),
    timezone: yup.string().required(),
    promotionId: yup
        .number()
        .notRequired()
        .nullable()
        .test('shouldBeDefined', 'Please select a promotion', function (v) {
            const type = this.resolve(yup.ref('type'));
            return !(type === 'promotion' && !v);
        }),
    items: yup.array(yupFeaturedFormItems).min(2).max(2).required(),
});

const YupFeaturedHistoryItem = yup.object({
    id: yup.number().required(),
    type: yup.string().required(),
    typeId: yup.number().nullable(),
    action: yup.string().required(),
    email: yup.string().nullable(),
    createdAt: yup.string().required(),
});

const YupFeaturedHistoryResponse = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(YupFeaturedHistoryItem).default([]),
});

export type Featured = yup.InferType<typeof yupFeatured>;
export type PaginatedFeatured = yup.InferType<typeof yupPaginatedList>;
export type FeaturedForm = yup.InferType<typeof yupFeaturedForm>;

export type SearchParams = {
    perPage?: number;
    page?: number;
    filter?: ActiveQueryParam;
    appTarget?: string;
};

export class FeaturedStore {
    @observable paginatedList: PaginatedFeatured | null = null;
    @observable isFetchingList = false;
    @observable visibleList: Featured[] = [];
    @observable isFetchingVisibleList = false;
    @observable isUpdatingVisibleList = false;
    @observable activeDatesMap: Map<string, number> = new Map();
    @observable isFetchingActiveDates = false;

    @observable featuredHistory: PaginatedList<CmsUserHistory> | null = null;
    @observable isFetchingFeaturedHistory = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    async getPaginatedList(params: SearchParams) {
        if (this.isFetchingList) {
            return;
        }

        runInAction(() => {
            this.isFetchingList = true;
        });

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/featured',
                params,
            },
            yupPaginatedList
        );

        runInAction(() => {
            this.isFetchingList = false;
            if (!response.err && response.data) {
                this.paginatedList = response.data;
            }
        });
    }

    @action
    async getVisibleList(action?: string) {
        if (this.isFetchingVisibleList) {
            return;
        }

        runInAction(() => {
            this.isFetchingVisibleList = true;
        });

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/featured/visible',
                params: action ? { action } : undefined,
            },
            yup.array(yupFeatured).required()
        );

        runInAction(() => {
            this.isFetchingVisibleList = false;
            if (!response.err && response.data) {
                this.setVisibleList(response.data);
            }
        });
    }

    @action
    setVisibleList(list: Featured[]) {
        this.visibleList = list.filter((featured) => !featured.geolocated);
    }

    @action
    async updateVisibleList() {
        const featuredIds = this.visibleList.map((f) => f.id);
        runInAction(() => {
            this.isUpdatingVisibleList = true;
        });

        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PATCH,
            url: '/featured/sort',
            data: { featuredIds },
        });
        runInAction(() => {
            this.isUpdatingVisibleList = false;

            if (!response.err) {
                NotificationManager.showSuccess('List updated');
            }
        });

        return !response.err;
    }

    @action
    async fetchActiveDatesMap() {
        if (this.isFetchingActiveDates) {
            return;
        }

        runInAction(() => {
            this.isFetchingActiveDates = true;
        });

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/featured/visible',
            },
            yup.array(yupFeatured).required()
        );

        runInAction(() => {
            this.isFetchingActiveDates = false;
            if (!response.err && response.data) {
                this.activeDatesMap = this.calculateActiveDatesMap(
                    response.data
                );
            }
        });
    }

    private calculateActiveDatesMap(banners: Featured[]): Map<string, number> {
        const datesMap = new Map<string, number>();

        banners.forEach((banner) => {
            const startDate = moment
                .tz(banner.startDate, banner.timezone)
                .startOf('day');
            const endDate = moment
                .tz(banner.endDate, banner.timezone)
                .startOf('day');

            const currentDate = startDate.clone();
            while (
                currentDate.isSameOrBefore(endDate, 'day') &&
                currentDate.diff(startDate, 'days') < MAX_DATE_RANGE_DAYS
            ) {
                const dateKey = currentDate.format('YYYY-MM-DD');
                datesMap.set(dateKey, (datesMap.get(dateKey) || 0) + 1);
                currentDate.add(1, 'day');
            }
        });

        return datesMap;
    }

    @action
    async fetchFeatured(this: FeaturedStore, featuredId: number) {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: `/featured/${featuredId}`,
            },
            yupFeatured
        );

        if (!response.err) {
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async createFeatured(this: FeaturedStore, data: FeaturedForm) {
        const response = await this.rootStore.makeNetworkCall<{
            id: number;
        }>({
            method: HttpMethod.POST,
            url: '/featured',
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Featured created');
            return response.data?.id ?? null;
        } else {
            throw response.err;
        }
    }

    @action
    async updateFeatured(
        this: FeaturedStore,
        featuredId: number,
        data: FeaturedForm
    ) {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PUT,
            url: `/featured/${featuredId}`,
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Featured updated');
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async deleteFeatured(this: FeaturedStore, featuredId: number) {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/featured/${featuredId}`,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Featured deleted');
        }

        return response;
    }

    @action
    async fetchFeaturedHistory(page = 1, perPage = 10, action?: string) {
        if (this.isFetchingFeaturedHistory) {
            return;
        }

        runInAction(() => {
            this.isFetchingFeaturedHistory = true;
        });

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/featured/history',
                params: { page, perPage, ...(action ? { action } : {}) },
            },
            YupFeaturedHistoryResponse
        );

        runInAction(() => {
            this.isFetchingFeaturedHistory = false;
            if (!response.err && response.data) {
                this.featuredHistory = {
                    page: response.data.page,
                    perPage: response.data.perPage,
                    total: response.data.total,
                    results: response.data.results.map((item) => ({
                        email: item.email ?? null,
                        action: item.action,
                        createdAt: item.createdAt,
                    })),
                };
            }
        });
    }
}
